/**
 * Pendo analytics + in-product messaging.
 *
 * DE reference: https://discoveryed.atlassian.net/wiki/spaces/developers/pages/1086386/Pendo
 *
 * The agent is injected lazily here rather than sitting in index.html: DE's Pendo contract
 * doesn't cover students, and the doc requires that we neither load *nor* initialize Pendo
 * for the student role. Injecting after the role is known keeps that guarantee in one place.
 */
import { useAppStore } from '@/stores/useAppStore';
import { useEmployeeStore } from '@/stores/useEmployeeStore';
import { useGroupAdminStore } from '@/stores/useGroupAdminStore';
import { useMainStore } from '@/stores/useMainStore';
import { useOrganizationStore } from '@/stores/useOrganizationStore';
import { useAuth0 } from '@/auth/index';
import { useDeAuth } from '@/auth/deAuth';
import { BLOCKED_ROLES } from '@/constants';
import { getOnboardingStatus } from '@/utils/auth/currentUserData';

// Must be a Pendo *application* key (app.pendo.io -> Subscription Settings -> App Details),
// not an integration key. Anything else 404s at the CDN, which fails silently - see loadPendoAgent.
const API_KEY = import.meta.env.VITE_PENDO_API_KEY;
const AGENT_SRC = API_KEY ? `https://cdn.pendo.io/agent/static/${API_KEY}/pendo.js` : null;

// Methods the install snippet stubs out so calls made before the agent lands are queued.
const AGENT_METHODS = ['initialize', 'identify', 'updateOptions', 'pageLoad', 'track'];

const ENV_MAP = {
    development: 'DEV',
    qa: 'QA',
    staging: 'STAGE',
    production: 'PROD',
};

const getEnvironment = () => ENV_MAP[import.meta.env.MODE?.toLowerCase()] ?? ENV_MAP.development;

const DEFAULT_COUNTRY = 'US';

const getSignInDate = () => {
    const now = new Date();

    return new Date(now.getTime() - now.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
};

// e.g. "en-US" -> { locale: 'en', country: 'US' }. Some browsers resolve to a bare "en".
const getLocaleData = () => {
    const { locale: resolved } = Intl.DateTimeFormat().resolvedOptions();
    const [locale, country] = (resolved || '').split('-');

    return { locale, country: country || DEFAULT_COUNTRY };
};

// Pendo only accepts strings, numbers and bools - drop anything we couldn't resolve.
const compact = (data) => Object.fromEntries(Object.entries(data).filter(([, value]) => value !== undefined && value !== null && value !== ''));

// A missing key or a dead agent URL is invisible otherwise: window.pendo stays the stub below
// and every queued call is silently dropped. Warn once - implementPendo retries on each
// appMode/loading change, so an un-guarded warning would repeat.
let warned = false;

const warnOnce = (message) => {
    if (warned) {
        return;
    }

    warned = true;
    console.warn(`[Pendo] ${message}`);
};

/**
 * Vendor install snippet, de-minified: stub window.pendo, queue any calls made before the
 * real agent arrives (initialize must stay at the front of the queue), then load the agent.
 * @returns {boolean} false when there's no key to load with, so callers can skip initialize().
 */
export const loadPendoAgent = () => {
    if (!AGENT_SRC) {
        warnOnce('VITE_PENDO_API_KEY is not set - skipping agent load.');

        return false;
    }

    if (document.querySelector(`head>script[src='${AGENT_SRC}']`)) {
        return true;
    }

    const agent = window.pendo || {};
    agent._q = agent._q || [];

    AGENT_METHODS.forEach((method, index) => {
        agent[method] =
            agent[method] ||
            ((...args) => {
                agent._q[index === 0 ? 'unshift' : 'push']([method, ...args]);
            });
    });

    window.pendo = agent;

    const script = document.createElement('script');
    script.async = true;
    script.onerror = () => {
        warnOnce(`Agent failed to load from ${AGENT_SRC} - check VITE_PENDO_API_KEY is a valid application key. Queued calls will be dropped.`);
    };
    script.src = AGENT_SRC;
    document.head.appendChild(script);

    return true;
};

/**
 * Builds the pendo.initialize() payload for the signed-in user.
 * Returns null when we don't have enough to identify them yet.
 */
export const getPendoMetadata = () => {
    const appStore = useAppStore();
    const employeeStore = useEmployeeStore();
    const groupAdminStore = useGroupAdminStore();
    const mainStore = useMainStore();
    const orgStore = useOrganizationStore();
    const auth = useAuth0();
    const de = useDeAuth();

    const { isAuthenticated: partnerAuthenticated } = auth;
    const deAuthenticated = !partnerAuthenticated && de.isAuthenticated;

    if (!partnerAuthenticated && !deAuthenticated) {
        return null;
    }

    const mode = appStore.appMode;

    // Group admins never load employee data - useSession's resolveAuth0Session hands off to
    // groupAdminHandler and returns before getEmployee runs - so their id lives in their own store.
    const userId = appStore.isGroupAdmin ? groupAdminStore.groupAdminId : employeeStore.userId;

    if (!mode || !userId) {
        return null;
    }

    const environment = getEnvironment();

    // Keep pre-prod usage off production visitor profiles.
    const envSuffix = environment === ENV_MAP.production ? '' : `-${environment}`;

    const visitor = {
        environment,
        careerConnectRole: mode,
        careerConnectOnboardingStatus: getOnboardingStatus({ appStore, mainStore, employeeStore }),
        authenticationType: deAuthenticated ? 'oauth2.discoveryed' : 'auth0',
        userAgent: window.navigator.userAgent,
        screenHeight: window.screen.height,
        screenWidth: window.screen.width,
        careerConnectLastSignIn: getSignInDate(),
        ...getLocaleData(),
    };

    if (deAuthenticated) {
        // getDEUser/getSite write the DE user (id + site) into mainStore.user.data.
        const { account_guid: accountGuid, account_name: accountName, site_guid: siteGuid, site_name: siteName } = mainStore.user.data.site || {};

        // DE users already exist in Pendo via app.discoveryeducation.com. GUIDs go up uppercased
        // (per the doc) so we land on the same visitor/account instead of creating a duplicate.
        // visitor.role is left alone for the same reason - DE Platform owns it, careerConnectRole
        // carries ours.
        return {
            visitor: compact({ ...visitor, id: `${userId.toUpperCase()}${envSuffix}` }),
            account: compact({ id: siteGuid?.toUpperCase(), name: siteName }),
            parentAccount: compact({ id: accountGuid?.toUpperCase(), name: accountName }),
        };
    }

    // Auth0 users (volunteers / org admins) are Career Connect records, not DE ones - namespace
    // their ids so they can't collide with a DE GUID on the shared Pendo subscription.
    return {
        visitor: compact({ ...visitor, id: `careerConnect-${userId}${envSuffix}`, role: mode }),
        account: compact({ id: orgStore.organizationId ? `careerConnect-${orgStore.organizationId}` : null, name: orgStore.organization?.name }),
    };
};

/**
 * Loads the agent and identifies the current user.
 * @returns {boolean} true when Pendo was initialized, false when it was skipped.
 */
const implementPendo = () => {
    const appStore = useAppStore();

    // DE's contract doesn't have the MAU allocation for students, and DE admins are internal.
    if (BLOCKED_ROLES.includes(appStore.appMode) || appStore.isDeAdmin) {
        return false;
    }

    const metadata = getPendoMetadata();

    if (!metadata) {
        return false;
    }

    if (!loadPendoAgent()) {
        return false;
    }

    window.pendo.initialize({ ...metadata });

    return true;
};

export default implementPendo;
